"use client";
import { useTranslations } from "next-intl";
import { Logger } from "@shared/utils/logger";
import { Config } from "@server/config/config";
import { Room, RemoteParticipant } from "livekit-client";
import { toastNotify } from "@client/components/ui/toast";
import { useAppSelector } from "@client/store/store.hooks";
import { useCallback, useState, useEffect, useRef } from "react";
import { ApproveUserPayload } from "@server/types/meeting.types";
import { JoinRequestPayload } from "@client/types/livekit.types";
import { approveUserForMeeting } from "@client/actions/meeting/meeting.actions";

interface UseJoinRequestsOptions {
  room: Room;
}

export interface JoinRequestWithTimer extends JoinRequestPayload {
  timeLeft: number;
  isGuest?: boolean;
  guestId?: string;
  isKickedOut?: boolean; // Flag for previously removed users
}

interface UseJoinRequestsReturn {
  joinRequests: JoinRequestWithTimer[] /** List of pending join requests */;
  handleJoinRequestDataReceived: (
    message: Record<string, unknown>,
    participant?: RemoteParticipant
  ) => void /** Handler for incoming join request messages via DataChannel */;
  approveRequest: (request: JoinRequestWithTimer) => Promise<boolean> /** Approve a user's join request */;
  denyRequest: (request: JoinRequestWithTimer) => Promise<boolean> /** Deny a user's join request */;
  removeRequest: (request: JoinRequestWithTimer) => void /** Remove a request from the list (internal use) */;
}

/**
 * Manages waiting room / lobby join requests.
 * Supports both regular users (userId) and guest users (guestId).
 */
export const useJoinRequests = ({ room }: UseJoinRequestsOptions): UseJoinRequestsReturn => {
  const [joinRequests, setJoinRequests] = useState<JoinRequestWithTimer[]>([]);

  /** Ref to access latest joinRequests in callbacks without re-creating them */
  const joinRequestsRef = useRef<JoinRequestWithTimer[]>([]);
  joinRequestsRef.current = joinRequests;

  const connectionDetails = useAppSelector((state) => state.connection.connectionDetails);
  const isHost = connectionDetails?.isHost;
  const hostId = connectionDetails?.hostId;
  const t = useTranslations();

  const removeRequest = useCallback((request: JoinRequestWithTimer) => {
    setJoinRequests((prev) => {
      const requestId = request.isGuest && request.guestId ? `guest-${request.guestId}` : `user-${request.userId}`;
      return prev.filter((r) => {
        const rId = r.isGuest && r.guestId ? `guest-${r.guestId}` : `user-${r.userId}`;
        return rId !== requestId;
      });
    });
  }, []);

  /**
   * Approves a user's join request via API.
   * Handles both authenticated users and guest users.
   */
  const approveRequest = useCallback(
    async (request: JoinRequestWithTimer): Promise<boolean> => {
      if (!request) return false;

      // Optimistic removal
      removeRequest(request);

      // Build payload based on user type
      const payload: ApproveUserPayload = request.isGuest
        ? {
            roomName: request.roomName,
            hostId: Number(hostId),
            approve: true,
            guestId: request.guestId,
            isGuest: true
          }
        : {
            roomName: request.roomName,
            userId: request.userId,
            hostId: Number(hostId),
            approve: true
          };

      try {
        const response = await approveUserForMeeting(payload);

        if (!response.status) {
          toastNotify.error(response.message || t("toast.joinRequest.approveFailed"));
          return false;
        }
        toastNotify.success(`${request.userName} ${t("toast.joinRequest.approved")}`);
        return true;
      } catch (error) {
        Logger.error("useJoinRequests", "Approve error:", error);
        toastNotify.error(t("toast.joinRequest.actionError"));
        return false;
      }
    },
    [hostId, removeRequest, t]
  );

  /**
   * Denies a user's join request via API.
   * Handles cleanup of the request from the list upon success.
   */
  const denyRequest = useCallback(
    async (request: JoinRequestWithTimer): Promise<boolean> => {
      if (!request) return false;

      // Optimistic removal
      removeRequest(request);

      // Build payload based on user type
      const payload: ApproveUserPayload = request.isGuest
        ? {
            roomName: request.roomName,
            hostId: Number(hostId),
            approve: false,
            guestId: request.guestId,
            isGuest: true
          }
        : {
            roomName: request.roomName,
            userId: request.userId,
            hostId: Number(hostId),
            approve: false
          };

      try {
        await approveUserForMeeting(payload);

        return true;
      } catch (error) {
        Logger.error("useJoinRequests", "Deny error:", error);
        toastNotify.error(t("toast.joinRequest.actionError"));
        return false;
      }
    },
    [hostId, removeRequest, t]
  );

  /**
   * Handles incoming join request messages from DataChannel.
   * Only processes if current user is the host.
   * Supports both regular users and guest users.
   */
  const handleJoinRequestDataReceived = useCallback(
    (message: Record<string, unknown>, participant?: RemoteParticipant) => {
      if (!isHost || message.type !== "join-request") return;

      const isGuest = Boolean(message.isGuest);
      const guestId = message.guestId ? String(message.guestId) : undefined;
      const userId = message.userId ? Number(message.userId) : 0;

      setJoinRequests((prev) => {
        // Check for duplicates
        const exists = prev.some((r) => {
          if (isGuest && guestId) {
            return r.isGuest && r.guestId === guestId;
          }
          return !r.isGuest && r.userId === userId;
        });

        if (exists) return prev;

        const newRequest: JoinRequestWithTimer = {
          type: "join-request",
          userId,
          userName: String(message.userName || "Unknown"),
          roomName: room.name,
          hostId: room.localParticipant.identity,
          senderIdentity: participant?.identity || String(message.senderIdentity || ""),
          fromIdentity: participant?.identity,
          fromSid: participant?.sid,
          timeLeft: Config.TIMER_SECONDS,
          isGuest,
          guestId,
          isKickedOut: Boolean(message.isKickedOut)
        };

        return [...prev, newRequest];
      });
    },
    [room, isHost]
  );

  /** Countdown timer - Updates timeLeft every second */
  useEffect(() => {
    if (joinRequests.length === 0) return;

    const interval = setInterval(() => {
      setJoinRequests((prev) => {
        return prev
          .map((req) => ({
            ...req,
            timeLeft: req.timeLeft - 1
          }))
          .filter((req) => req.timeLeft > 0);
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [joinRequests.length]);

  return {
    joinRequests,
    handleJoinRequestDataReceived,
    approveRequest,
    denyRequest,
    removeRequest
  };
};
